import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/projects/${id}`);
        setProject(response.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-dark">Loading...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <p className="text-dark mb-4">Project not found</p>
          <Link to="/" className="text-green hover:underline">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="project-detail-page" className="bg-cream text-dark min-h-screen">
      <div className="pt-32 pb-24">
        <div className="container mx-auto px-6 md:px-12 max-w-[1200px]">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-dark hover:text-green transition-colors mb-12"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          <div className="flex flex-wrap gap-2 mb-6">
            {project.tags.map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded-full text-xs uppercase tracking-wider bg-yellow text-dark border border-dark/20"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1
            data-testid="project-title"
            className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase mb-8 text-green"
            style={{ fontFamily: "'Anton', sans-serif" }}
          >
            {project.title}
          </h1>

          <p className="text-xl md:text-2xl leading-relaxed font-light mb-12 text-dark/80">
            {project.description}
          </p>

          {project.video_url && (
            <div className="mb-12 aspect-video w-full bg-dark rounded-lg overflow-hidden shadow-lg">
              {project.video_url.includes('vimeo') ? (
                <iframe
                  src={project.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={project.title}
                  className="w-full h-full"
                />
              ) : project.video_url.includes('youtube') ? (
                <iframe
                  src={project.video_url.replace('watch?v=', 'embed/')}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={project.title}
                  className="w-full h-full"
                />
              ) : (
                <video
                  controls
                  className="w-full h-full object-cover"
                  src={project.video_url}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}

          {!project.video_url && (
            <div className="mb-12">
              <img
                src={project.thumbnail}
                alt={project.title}
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          )}

          {project.content && (
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed text-dark/80">{project.content}</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
